// RFI tab -> Pending / Archive (owner 2026-11-02: "pulls their history into RFI archive tab
// and RFI pending tab, that logs the RFI id/number, the RFI name, the RFI responsible, the
// RFI content, text and the RFI status, the RFI due date, the RFI filing date").
//
// The RFIs read from the project's Procore and ACC projects (back/utils/rfiFeed), once a day
// and on "Read now". Closed and void RFIs are the archive; everything else is pending. The
// provider is the record: nothing here edits an RFI, and each row links to it there.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Input, Space, Table, Tag, Tooltip, Typography } from "antd";
import { ApiOutlined, ExportOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { listFeedRfis } from "api/rfi/rfiApi";
import RfiSourcesDrawer from "./RfiSourcesDrawer";

const { Text, Paragraph } = Typography;
const PROVIDER = { procore: "Procore", acc: "ACC" };

const statusColor = (s, bucket) => {
  if (bucket === "archived") return "default";
  const k = String(s || "").toLowerCase();
  if (k === "draft") return "default";
  if (k.startsWith("answer")) return "purple";
  if (k === "rejected") return "red";
  return "blue";
};
const day = (v) => (v ? moment(v).format("MMM D, YYYY") : "");
const people = (list) => (list || []).map((p) => p.name || p.email || p.id).filter(Boolean).join(", ");

function Detail({ r }) {
  return (
    <div style={{ maxWidth: 1000 }}>
      <Text strong>Question</Text>
      <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 12 }}>{r.question || <Text type="secondary">(no question text)</Text>}</Paragraph>
      <Text strong>Responses</Text>
      {(r.responses || []).length ? (r.responses || []).map((x, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} style={{ marginBottom: 8 }}>
          <Text type="secondary">{[x.by, day(x.at)].filter(Boolean).join(" · ")}</Text>
          {x.official && <Tag color="green" style={{ marginLeft: 6 }}>Official</Tag>}
          <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>{x.text}</Paragraph>
        </div>
      )) : <Paragraph type="secondary">No response yet</Paragraph>}
      {(r.earlierRevisions || []).map((o) => (
        <div key={o.revision} style={{ marginBottom: 12, paddingLeft: 12, borderLeft: "3px solid #d9d9d9" }}>
          <Text strong>Revision {o.revision}</Text>{" "}
          <Text type="secondary">{[o.status === "closed_with_revision" ? "closed - revised" : o.status, o.filedAt && `filed ${day(o.filedAt)}`, o.closedAt && `closed ${day(o.closedAt)}`].filter(Boolean).join(" \u00b7 ")}</Text>
          {o.question && <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 4 }}>{o.question}</Paragraph>}
          {(o.responses || []).map((x, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Paragraph key={i} type="secondary" style={{ whiteSpace: "pre-wrap", marginBottom: 4 }}>{[x.by, day(x.at)].filter(Boolean).join(" \u00b7 ")}{x.official ? " (official)" : ""}: {x.text}</Paragraph>
          ))}
        </div>
      ))}
      <Space size={16} wrap>
        {r.manager && r.manager.name && <Text type="secondary">RFI manager: {r.manager.name}</Text>}
        {r.location && <Text type="secondary">Location: {r.location}</Text>}
        {r.closedAt && <Text type="secondary">Closed {day(r.closedAt)}</Text>}
        {r.attachments > 0 && <Text type="secondary">{r.attachments} attachment{r.attachments === 1 ? "" : "s"} on {PROVIDER[r.provider]}</Text>}
        {(r.history || []).length > 1 && (
          <Text type="secondary">History: {(r.history || []).map((h) => `${h.status} (${day(h.at)})`).join(" → ")}</Text>
        )}
        {r.link && <a href={r.link} target="_blank" rel="noopener noreferrer"><ExportOutlined /> Open in {PROVIDER[r.provider]}</a>}
      </Space>
    </div>
  );
}

export default function RfiFeedTab({ projectId, bucket, onCounts }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [sourcesOpen, setSourcesOpen] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    setError("");
    listFeedRfis(projectId, bucket)
      .then((d) => { setData(d); if (onCounts) onCounts(d.counts); })
      .catch((e) => { setData(null); setError(e.message || "Could not load the RFIs"); });
  }, [projectId, bucket, onCounts]);
  useEffect(() => { setData(null); load(); }, [load]);

  const rows = useMemo(() => {
    const all = (data && data.rfis) || [];
    const s = q.trim().toLowerCase();
    if (!s) return all;
    return all.filter((r) => [r.number, r.revision > 0 ? `rev ${r.revision}` : "", r.title, r.status, r.question, people(r.responsible)].join(" ").toLowerCase().includes(s));
  }, [data, q]);

  const columns = [
    {
      title: "#", dataIndex: "number", width: 100,
      // a revised RFI keeps its number; which revision this is goes beside it
      render: (v, r) => (r.revision > 0 ? <span>{v} <Tag style={{ marginLeft: 2 }}>Rev {r.revision}</Tag></span> : v),
      sorter: (a, b) => String(a.number).localeCompare(String(b.number), undefined, { numeric: true }) },
    { title: "Name", dataIndex: "title", ellipsis: true },
    { title: "Responsible", width: 200, ellipsis: true, render: (_, r) => people(r.responsible) },
    { title: "Status", dataIndex: "status", width: 120, render: (v, r) => <Tag color={statusColor(v, r.bucket)}>{v || "unknown"}</Tag> },
    {
      title: "Due", dataIndex: "dueDate", width: 120,
      sorter: (a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0),
      render: (v, r) => {
        if (!v) return "";
        const late = r.bucket === "pending" && moment(v).isBefore(moment(), "day");
        return late ? <Tooltip title="Past due"><Text type="danger">{day(v)}</Text></Tooltip> : day(v);
      },
    },
    { title: "Filed", dataIndex: "filedAt", width: 120, defaultSortOrder: "descend", sorter: (a, b) => new Date(a.filedAt || 0) - new Date(b.filedAt || 0), render: day },
    { title: "From", dataIndex: "provider", width: 90, render: (v) => PROVIDER[v] || v },
  ];

  const tunnels = (data && data.tunnels) || [];
  const lastRead = tunnels.map((t) => t.lastRun && t.lastRun.at).filter(Boolean).sort().pop();
  const failing = tunnels.filter((t) => t.lastRun && t.lastRun.error);

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input.Search allowClear placeholder="Search number, name, responsible, text" style={{ width: 320 }} value={q} onChange={(e) => setQ(e.target.value)} />
        <Button icon={<ApiOutlined />} onClick={() => setSourcesOpen(true)}>Procore / ACC sources{tunnels.length ? ` (${tunnels.length})` : ""}</Button>
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
        <Text type="secondary">
          {tunnels.length ? `Read once a day${lastRead ? ` · last read ${moment(lastRead).fromNow()}` : ""}` : ""}
        </Text>
      </Space>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {failing.map((t) => (
        <Alert key={t._id} type="warning" showIcon style={{ marginBottom: 8 }}
          message={`${PROVIDER[t.provider]} ${t.path || t.containerId}: ${t.lastRun.error}`} />
      ))}
      <Table
        rowKey="_id" size="middle" columns={columns} loading={!data && !error}
        dataSource={rows}
        expandable={{ expandedRowRender: (r) => <Detail r={r} />, expandRowByClick: true }}
        locale={{
          emptyText: (
            <Empty description={tunnels.length
              ? (bucket === "archived" ? "No closed RFIs yet" : "No open RFIs")
              : "No Procore or ACC project is read into this one yet - add one under Procore / ACC sources"} />
          ),
        }}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        scroll={{ x: 900 }}
      />
      {sourcesOpen && (
        <RfiSourcesDrawer projectId={projectId} onClose={() => { setSourcesOpen(false); load(); }} />
      )}
    </div>
  );
}
